import { useFrontendConfigContext } from '@apeiron/config'
import { ListingStatus } from '@apeiron/core'
import { Planet, PlanetStatus, PrimevalLegacy } from '@apeiron/planet'
import {
  ApeironCloseButton,
  formatYearOld,
  useAccountContext,
  useDate
} from '@apeiron/temp'
import { Box, Dialog, DialogContent, styled } from '@mui/material'
import { CooldownProgress } from '@src/components/planet/detail/planetAboutSection/CooldownProgress'
import { PlanetElementMap } from '@src/components/planet/detail/planetAboutSection/PlanetElementMap'
import { SectionContent } from '@src/components/planet/detail/SectionContent'
import ApeironLabelTextfield from '@src/components/share/apeiron/ApeironLabelTextfield'
import ApeironOwnerView from '@src/components/share/apeiron/ApeironOwnerView'
import { ROUTE } from '@src/constants/route'
import { usePlanetClassContext } from '@src/contexts/planet/PlanetClassContext'
import { usePrimevalLegacyContext } from '@src/contexts/planet/PrimevalLegacyContext'
import { add, isBefore, sub } from 'date-fns'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const StyledSectionContent = styled(SectionContent)`
  .Custom-Inner-Box {
    display: flex;
    flex-direction: column;

    gap: 6px;
  }
`

const ItemContainer = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 36px;
  grid-row-gap: 13px;
  align-items: flex-end;
`

const StyledCooldownProgress = styled(CooldownProgress)`
  width: 300px;
  margin-bottom: 18px;
`

const StyledApeironOwnerView = styled(ApeironOwnerView)`
  margin-top: 10px;
`

const AboutField = styled(ApeironLabelTextfield)`
  flex: 1;
`

const DialogTitleSection = styled('div')`
  display: flex;
  flex-direction: column;
  background-color: #262829;
`

const StyledCloseButton = styled(ApeironCloseButton)`
  align-self: flex-end;
  margin: 20px;
`

const StyledDialogContent = styled(DialogContent)`
  background-color: #262829;
`

export const PlanetAboutSection: FC<Props> = props => {
  const { className, planet } = props

  const router = useRouter()

  const { config } = useFrontendConfigContext()

  const { account } = useAccountContext()

  const { dateValid } = useDate()

  const [conjunctionCD, setConjunctionCD] = useState<number>(0)

  const [trackCD, setTrackCD] = useState<number>(0)

  const [openPopup, setOpenPopup] = useState(false)

  const { t } = useTranslation()

  const { planetClasses } = usePlanetClassContext()

  const planetClass = useMemo(() => {
    return R.find(item => item.type === planet.planetClass, planetClasses)
  }, [planet.planetClass, planetClasses])

  function getPrimevalLegacyToolTipTitle(
    primevalLegacyData: PrimevalLegacy
  ): string {
    const elementValues = []

    if (primevalLegacyData.fire !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.fire,
          element: t('element.fire')
        })
      )
    }

    if (primevalLegacyData.water !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.water,
          element: t('element.water')
        })
      )
    }

    if (primevalLegacyData.air !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.air,
          element: t('element.air')
        })
      )
    }

    if (primevalLegacyData.earth !== 0) {
      elementValues.push(
        t('planet.detail.about.item.tool_tip_title.primeval_legacy_content', {
          value: primevalLegacyData.earth,
          element: t('element.earth')
        })
      )
    }

    return t('planet.detail.about.item.tool_tip_title.primeval_legacy', {
      values: elementValues.join(', ')
    })
  }

  const primevalLegacyContext = usePrimevalLegacyContext()

  const primevalLegacy = planet.primevalLegacy
    ? primevalLegacyContext.getPrimevalLegacy(planet.primevalLegacy)
    : undefined

  const primevalLegacyName = primevalLegacy
    ? primevalLegacy.name
    : t('planet.detail.about.item.value.primeval_legacy.unknown', {
        primevalLegacyId: planet.primevalLegacy
      })

  const primevalLegacyToolTipTitle = primevalLegacy
    ? getPrimevalLegacyToolTipTitle(primevalLegacy)
    : undefined

  const now = new Date()

  const lastBreedTime = (planet.breedAt || '').toString()

  const hasLastBreed = dateValid(lastBreedTime)

  const conjunctStartCD = hasLastBreed ? new Date(lastBreedTime) : undefined

  const conjunctEndCD = conjunctStartCD
    ? add(conjunctStartCD, { seconds: conjunctionCD })
    : undefined

  const conjunctCDing = conjunctEndCD ? isBefore(now, conjunctEndCD) : false

  const { trackCanAttachAt: trackEndCD } = planet

  const trackStartCD = !R.isNil(trackEndCD)
    ? sub(trackEndCD, { seconds: trackCD })
    : null

  const trackCDing = !R.isNil(trackEndCD) ? isBefore(now, trackEndCD) : false

  useEffect(() => {
    if (!R.isNil(config)) {
      setConjunctionCD(config.breed.cooldown)
      setTrackCD(config.attach.cooldown)
    }
  }, [config])

  const handleOnOpenPopup = useCallback(() => {
    setOpenPopup(true)
  }, [])

  const handleOnClosePopup = useCallback(() => {
    setOpenPopup(false)
  }, [])

  const PlanetElementPopup = useMemo(() => {
    return (
      <Dialog open={openPopup} onClose={handleOnClosePopup}>
        <DialogTitleSection>
          <StyledCloseButton onClick={handleOnClosePopup} />
        </DialogTitleSection>
        <StyledDialogContent>
          <PlanetElementMap />
        </StyledDialogContent>
      </Dialog>
    )
  }, [handleOnClosePopup, openPopup])

  const handleAgeLinkClick = useCallback(() => {
    router.push({
      pathname: ROUTE.SANCTUARY_CHRONOMERGE,
      query: { mainPlanetId: planet.planetID }
    })
  }, [planet.planetID, router])

  const planetCanChronomerge = useMemo(() => {
    const notListed = planet.nftListingStatus === ListingStatus.UNLISTING

    const notStaked = planet.status === PlanetStatus.Enable

    const isOwner = planet.owner?.walletAddress === account.walletAddress

    return isOwner && notListed && notStaked
  }, [planet, account])

  return (
    <StyledSectionContent className={className}>
      {conjunctCDing && (
        <StyledCooldownProgress
          title={t('planet.date_progress_bar.conjuction.title')}
          startAt={conjunctStartCD as Date}
          endAt={conjunctEndCD as Date}
          now={now}
        />
      )}
      {trackCDing && (
        <StyledCooldownProgress
          title={t('planet.date_progress_bar.orbital_detach.title')}
          startAt={trackStartCD as Date}
          endAt={trackEndCD as Date}
          now={now}
        />
      )}
      <ItemContainer>
        <AboutField
          title={t('planet.detail.about.item.title.planet_type')}
          text={planet.planetType}
          infoButtonPopup={{
            popup: PlanetElementPopup,
            onInfoClick: handleOnOpenPopup
          }}
        />
        <AboutField
          title={t('planet.detail.about.item.title.class')}
          text={planetClass?.name}
          explain={t('planet.detail.about.item.tool_tip_title.avatar_class')}
        />
        <AboutField
          title={t('planet.detail.about.item.title.breed_count')}
          text={`${planet.breedCount}/${planet.breedCountMax}`}
          explain={t('planet.detail.about.item.tool_tip_title.breed_count')}
        />
        <AboutField
          title={t('planet.detail.about.item.title.age')}
          text={formatYearOld(planet.age)}
          explain={t('planet.detail.about.item.tool_tip_title.aging')}
          {...(planetCanChronomerge
            ? {
                linkButton: {
                  name: t('sanctuary.chronomerge.button.chronomerge'),
                  onLinkClick: handleAgeLinkClick
                }
              }
            : {})}
        />
        <AboutField
          title={t('planet.detail.about.item.title.battle_pass')}
          text={
            planet.battlePassLock
              ? t('planet.detail.about.item.value.battle_pass_claimed')
              : t('planet.detail.about.item.value.battle_pass_unclaimed')
          }
          explain={t('planet.detail.about.item.tool_tip_title.battle_pass')}
        />
        {primevalLegacy && (
          <AboutField
            title={t('planet.detail.about.item.title.primeval_legacy')}
            text={primevalLegacyName}
            explain={primevalLegacyToolTipTitle}
          />
        )}
      </ItemContainer>
      {planet.owner && (
        <StyledApeironOwnerView
          owner={planet.owner}
          route={ROUTE.OTHER_INVENTORY_PLANET_LIST}
        />
      )}
    </StyledSectionContent>
  )
}

type Props = {
  className?: string
  planet: Planet
}
